/**
 * Deterministic multi-stop “mesh” background from a coupon name.
 * Palette: blossom pinks, seafoam & jade greens, sky & pool blues.
 */

const FLOWER = [
  [255, 182, 193],
  [255, 192, 203],
  [255, 209, 220],
  [255, 218, 230],
  [248, 200, 220],
  [236, 201, 224],
  [255, 171, 203],
  [244, 194, 235],
  [230, 210, 255],
];

const TREE = [
  [72, 201, 176],
  [64, 179, 162],
  [102, 187, 160],
  [56, 142, 120],
  [80, 200, 170],
  [120, 200, 180],
  [135, 206, 235],
  [154, 208, 236],
  [125, 180, 220],
  [100, 180, 200],
  [90, 170, 210],
  [72, 160, 190],
];

const MIST = [
  [240, 252, 255],
  [242, 250, 255],
  [245, 252, 250],
  [248, 252, 252],
  [255, 248, 252],
  [250, 248, 255],
  [238, 248, 252],
  [244, 252, 248],
];

function fnv1a32(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function pick(arr, h, salt) {
  // Bitwise ^ uses signed 32-bit; combine with >>> 0 so % is never negative.
  const i = ((h ^ salt) >>> 0) % arr.length;
  return arr[i];
}

function rgb([r, g, b], a = 1) {
  return a === 1 ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${a})`;
}

/** 0..1 from hash nibble stream */
function u32(h, i) {
  return ((h >>> (i * 11)) & 0xfff) / 0xfff;
}

/**
 * @param {string} seed — stable per coupon (e.g. name)
 * @returns {string} CSS `background` value
 */
export function couponMeshGradient(seed) {
  const s = String(seed || "coupon");
  const h = fnv1a32(s);

  const f1 = pick(FLOWER, h, 1);
  const f2 = pick(FLOWER, h, 2);
  const t1 = pick(TREE, h, 3);
  const t2 = pick(TREE, h, 4);
  const t3 = pick(TREE, h, 5);
  const baseA = pick(MIST, h, 6);
  const baseB = pick(MIST, h, 7);

  const angle = (h % 140) + 100;

  const x1 = 8 + u32(h, 0) * 55;
  const y1 = 5 + u32(h, 1) * 45;
  const x2 = 35 + u32(h, 2) * 60;
  const y2 = 40 + u32(h, 3) * 55;
  const x3 = 5 + u32(h, 4) * 50;
  const y3 = 55 + u32(h, 5) * 40;
  const x4 = 50 + u32(h, 6) * 45;
  const y4 = 0 + u32(h, 7) * 35;

  const a1 = 0.72 + u32(h, 8) * 0.2;
  const a2 = 0.55 + u32(h, 9) * 0.22;
  const a3 = 0.5 + u32(h, 10) * 0.25;
  const a4 = 0.45 + u32(h, 11) * 0.2;

  const layers = [
    `radial-gradient(ellipse 95% 85% at ${x1.toFixed(1)}% ${y1.toFixed(1)}%, ${rgb(f1, a1)} 0%, transparent 58%)`,
    `radial-gradient(ellipse 80% 100% at ${x2.toFixed(1)}% ${y2.toFixed(1)}%, ${rgb(t1, a2)} 0%, transparent 52%)`,
    `radial-gradient(ellipse 110% 70% at ${x3.toFixed(1)}% ${y3.toFixed(1)}%, ${rgb(f2, a3)} 0%, transparent 55%)`,
    `radial-gradient(ellipse 75% 90% at ${x4.toFixed(1)}% ${y4.toFixed(1)}%, ${rgb(t2, a4)} 0%, transparent 48%)`,
    `radial-gradient(circle at 50% 100%, ${rgb(t3, 0.28)} 0%, transparent 45%)`,
    `linear-gradient(${angle}deg, ${rgb(baseA)}, ${rgb(baseB)})`,
  ];

  return layers.join(", ");
}
